class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[name="updates[]"]')) {
        event.stopPropagation();
        event.target.closest('cart-drawer-items')?.validateQuantity(event);
        return;
      }
      if (!event.target.matches('[data-cart-delivery-frequency]')) return;
      event.stopPropagation();
      this.updateDeliveryFrequency(event.target);
    }, true);
    this.addEventListener('submit', event => {
      if (this.cartMutationPending || this.cartRefreshRequired) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, true);
    this.addEventListener('click', event => {
      if ((this.cartMutationPending || this.cartRefreshRequired) && event.target.closest('[name="checkout"]')) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, true);
  }

  connectedCallback() {
    this.historyReturnHandler = (event) => {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (event.persisted || navigation?.type === 'back_forward') this.refreshAfterHistoryReturn();
    };
    window.addEventListener('pageshow', this.historyReturnHandler);
    const names = this.querySelector('[data-cart-product-names]');
    this.compactProductNames = names ? JSON.parse(names.textContent) : {};
    const intros = this.querySelector('[data-cart-product-intros]');
    this.compactProductIntros = intros ? JSON.parse(intros.textContent) : {};
    this.compactTitlesObserver = new MutationObserver(() => {
      cancelAnimationFrame(this.compactTitlesFrame);
      this.compactTitlesFrame = requestAnimationFrame(() => this.updateCompactTitles());
    });
    this.compactTitlesObserver.observe(this, { childList: true, subtree: true, characterData: true, attributes:true, attributeFilter:['aria-hidden','class'] });
    this.recommendationResizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.recommendationResizeFrame);
      this.recommendationResizeFrame = requestAnimationFrame(() => this.updateRecommendationHeights());
    });
    this.observedRecommendationCards = new WeakSet();
    this.updateCompactTitles();
  }


  async refreshAfterHistoryReturn() {
    if (!this.beginCartMutation()) return;
    let focusAfterRefresh;
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { 'Accept': 'application/json' }, cache: 'no-store'
      });
      if (!response.ok) throw new Error('Cart refresh failed');
      const cart = await response.json();
      await this.fetchCartSections(cart, { cache: 'no-store' });
      this.renderContents(cart, { preserveOpenState: true });
      focusAfterRefresh = this.querySelector('.drawer__close');
    } catch (error) {
      this.requireCartRefresh();
      console.error('Cart history refresh failed:', error);
    } finally {
      this.endCartMutation(focusAfterRefresh);
    }
  }

  beginCartMutation() {
    if (this.cartMutationPending || this.cartRefreshRequired) return false;
    this.cartMutationPending = true;
    this.setAttribute('aria-busy', 'true');
    this.mutationControls = new Map();
    this.lockCartControls();
    return true;
  }

  lockCartControls() {
    if (!this.cartMutationPending) return;
    this.querySelectorAll('button, input, select').forEach(control => {
      if (control.classList.contains('drawer__close')) return;
      if (!this.mutationControls.has(control)) this.mutationControls.set(control, control.disabled);
      if (!control.disabled) control.disabled = true;
    });
  }

  endCartMutation(focusTarget) {
    this.cartMutationPending = false;
    this.mutationControls?.forEach((disabled, control) => {
      if (control.isConnected) control.disabled = disabled;
    });
    this.mutationControls?.clear();
    this.removeAttribute('aria-busy');
    if (this.cartRefreshRequired) {
      this.querySelectorAll('[name="checkout"]').forEach(control => control.disabled = true);
    }
    if (focusTarget?.isConnected && this.classList.contains('active')) {
      removeTrapFocus(); trapFocus(this.querySelector('#CartDrawer'), focusTarget);
    }
  }

  requireCartRefresh() {
    this.cartRefreshRequired = true;
    const errors = this.querySelector('#CartDrawer-CartErrors');
    if (errors) {
      errors.textContent = 'We could not confirm the latest cart. Please refresh before checking out. ';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--secondary button--small';
      retry.textContent = 'Refresh cart';
      retry.addEventListener('click', () => window.location.reload());
      errors.appendChild(retry);
    }
    this.querySelectorAll('[name="checkout"]').forEach(control => control.disabled = true);
  }

  async fetchCartSections(data, fetchOptions = {}) {
    if (data.sections?.['cart-drawer'] && data.sections?.['cart-icon-bubble']) return data;
    const response = await fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble', fetchOptions);
    if (!response.ok) throw new Error('Cart refresh failed');
    data.sections = await response.json();
    if (!data.sections?.['cart-drawer'] || !data.sections?.['cart-icon-bubble']) throw new Error('Cart refresh failed');
    return data;
  }

  async upgradeSubscription(button) {
    if (!this.beginCartMutation()) return;
    const originalLabel = button.textContent;
    const status = button.parentElement.querySelector('[data-cart-upgrade-status]');
    let saved = false;
    let rejected = false;
    let focusAfterSave;
    try {
      const item = JSON.parse(button.dataset.upgradeToSubscription);
      if (!item.key || !Number(item.selling_plan_id)) { rejected = true; throw new Error('Subscription unavailable'); }
      button.textContent = 'Updating…';
      if (status) { status.setAttribute('role','status'); status.textContent = 'Updating subscription'; }
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({id:item.key, quantity:Number(item.quantity), selling_plan:Number(item.selling_plan_id),
          sections:this.getSectionsToRender().map(section=>section.id), sections_url:window.location.pathname})
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) { rejected = true; throw new Error('Subscription update rejected'); }
      saved = true;
      await this.fetchCartSections(data);
      this.renderContents(data, {preserveOpenState:true});
      const updated = [...this.querySelectorAll('[data-cart-delivery-frequency]')].find(control =>
        control.dataset.variantId === String(item.variant_id) && control.value === String(item.selling_plan_id));
      focusAfterSave = updated;
    } catch (error) {
      if (status?.isConnected) {
        status.setAttribute('role','alert');
        status.textContent = 'Unable to update subscription. Please try again.';
      }
      if (saved || !rejected) this.requireCartRefresh();
      console.error('Subscription upgrade failed:', error);
    } finally {
      if (button.isConnected) button.textContent = originalLabel;
      this.endCartMutation(focusAfterSave);
    }
  }

  updateRecommendationAccessibility() {
    this.querySelectorAll('.rebuy-product-block').forEach(card => {
      const inactive = card.getAttribute('aria-hidden') === 'true';
      if (inactive && card.contains(document.activeElement)) {
        const widget = card.closest('.rebuy-widget');
        const control = widget?.querySelector('button.flickity-prev-next-button:not([disabled])') || this.querySelector('.drawer__close');
        control?.focus();
      }
      if (card.inert !== inactive) card.inert = inactive;
    });
  }

  async updateDeliveryFrequency(select) {
    if (this.deliveryUpdatePending || select.value === select.dataset.currentPlan || !this.beginCartMutation()) return;
    this.deliveryUpdatePending = true;
    const previousPlan = select.dataset.currentPlan;
    const requestedPlan = select.value;
    const variantId = select.dataset.variantId;
    const messages = window.nutraceneCartDeliveryStrings;
    const status = select.closest('.nutracene-delivery-frequency').querySelector('[data-cart-delivery-status]');
    status.setAttribute('role', 'status');
    status.textContent = messages.updating;
    let saved = false;
    let rejected = false;
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: select.dataset.key,
          quantity: Number(select.dataset.qty),
          selling_plan: Number(requestedPlan),
          sections: this.getSectionsToRender().map(section => section.id),
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) { rejected = true; throw new Error(data.description || messages.error); }
      saved = true;
      await this.fetchCartSections(data);
      this.renderContents(data, { preserveOpenState: true });
      const updated = Array.from(this.querySelectorAll('[data-cart-delivery-frequency]')).find(control => control.dataset.variantId === variantId && control.value === requestedPlan);
      if (updated) {
        updated.closest('.nutracene-delivery-frequency').querySelector('[data-cart-delivery-status]').textContent = messages.updated;
        if (this.classList.contains('active')) {
          removeTrapFocus();
          trapFocus(this.querySelector('#CartDrawer'), updated);
        }
      }
    } catch (error) {
      select.value = saved ? requestedPlan : previousPlan;
      select.dataset.currentPlan = saved ? requestedPlan : previousPlan;
      status.textContent = saved ? messages.refreshError : messages.error;
      status.setAttribute('role', 'alert');
      if (saved || !rejected) this.requireCartRefresh();
      console.error('Delivery frequency update failed:', error);
    } finally {
      const updated = [...this.querySelectorAll('[data-cart-delivery-frequency]')].find(control => control.dataset.variantId === variantId && control.value === requestedPlan);
      this.endCartMutation(updated);
      this.deliveryUpdatePending = false;
    }
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.historyReturnHandler);
    this.compactTitlesObserver?.disconnect();
    this.recommendationResizeObserver?.disconnect();
    cancelAnimationFrame(this.recommendationResizeFrame);
    cancelAnimationFrame(this.compactTitlesFrame);
  }

  updateCompactTitles() {
    this.lockCartControls();
    this.updateRecommendationAccessibility();
    this.querySelectorAll('.rebuy-product-title-link').forEach((link) => {
      const handle = new URL(link.href, location.origin).pathname.match(/\/products\/([^/]+)/)?.[1];
      const name = this.compactProductNames[handle];
      if (name && link.textContent.trim() !== name) link.textContent = name;
      const intro = this.compactProductIntros[handle];
      const info = link.closest('.rebuy-product-info');
      if (intro && info) {
        let description = info.querySelector('.rebuy-product-description');
        if (!description) {
          description = document.createElement('div');
          description.className = 'rebuy-product-description';
          info.appendChild(description);
        }
        if (description.textContent !== intro) description.textContent = intro;
      }
    });
    this.querySelectorAll('.rebuy-product-block').forEach((card) => {
      if (!this.observedRecommendationCards.has(card)) {
        this.observedRecommendationCards.add(card);
        this.recommendationResizeObserver.observe(card);
      }
    });
    this.updateRecommendationHeights();
  }

  updateRecommendationHeights() {
    this.querySelectorAll('.flickity-viewport').forEach((viewport) => {
      if (!viewport.offsetWidth) return;
      const cards = Array.from(viewport.querySelectorAll('.rebuy-product-block'));
      if (!cards.length) return;
      const height = Math.ceil(Math.max(...cards.map((card) => {
        const style = getComputedStyle(card);
        return card.offsetHeight + parseFloat(style.marginTop || 0) + parseFloat(style.marginBottom || 0);
      }))) + 8;
      if (viewport.style.height !== height + 'px') viewport.style.setProperty('height', height + 'px', 'important');
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.lockCartControls();
    this.updateRecommendationAccessibility();
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (!options.preserveOpenState) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
